import "./formUtils.scss";

import React, { useState } from "react";

import { CButton } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { Can, authorizedUser } from "src/helpers/Auth";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { freeSet } from "@coreui/icons";
import Tooltip from "@mui/material/Tooltip";
import { Button } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { ReportType } from "src/interfaces/enums";
import { nuclearn_api_url } from "src/App";
const formatItemNameForHtmlId = (itemName) => {
  // lowercase the item name and replace spaces with dashes
  // Item name "Automation Config Template"
  // becomes "create-automation-config-template-button"
  return itemName?.toLowerCase().replace(/ /g, "-");
};

export const MenuButton = (props) => {
  let variant = props.variant;
  if (props.selected) {
    if (variant === "outline") {
      variant = "";
    } else if (variant === "") {
      variant = "outline";
    }
  }
  return (
    <Tooltip title={props.text} placement="top" arrow>
      <div style={{ display: "inline-block" }}>
        <CButton
          id={props.id ?? ""}
          data-testid={props["data-testid"]}
          className={`${props.className}`}
          color={props.color}
          disabled={props.selected}
          variant={props.variant}
          size={props.size}
          onClick={props.onClick}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              justifyContent: "center",
            }}
          >
            {props.includeIcons && <>{props.icon}</>}
            <span>{props.text}</span>
          </div>
        </CButton>
      </div>
    </Tooltip>
  );
};

const menuButtonPropTypes = {
  text: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["", "outline"]).isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg"]).isRequired,
  selected: PropTypes.bool,
  includeIcons: PropTypes.bool,
  icon: PropTypes.node.isRequired,
};

MenuButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  buttonVisible: PropTypes.bool,
  ...menuButtonPropTypes,
};

MenuButton.defaultProps = {
  includeIcons: true,
  selected: false,
  buttonVisible: false,
};

export const ModalMenuButton = (props) => {
  const [showModal, setShowModal] = React.useState(false);
  return (
    <>
      <div
        style={{
          position: "absolute",
          textAlign: "left",
        }}
      >
        {React.createElement(props.menuModal, {
          isVisible: showModal,
          onClose: () => {
            setShowModal(false);
          },
          ...props.menuModalProps,
        })}
      </div>
      <MenuButton onClick={() => setShowModal(true)} {...props} />
    </>
  );
};

ModalMenuButton.propTypes = {
  menuModal: PropTypes.elementType.isRequired,
  menuModalProps: PropTypes.object,
  ...menuButtonPropTypes,
};

export const CreateItemButton = (props) => {
  // Helper function for a standard way to render a item creation button and modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  return (
    <>
      <Tooltip
        title={props.tooltipTitle || props.itemName}
        placement="top"
        arrow
      >
        <div>
          <Button
            id={`${formatItemNameForHtmlId(props.itemName)}-button`}
            data-testid={props["data-testid"]}
            className={`create-${formatItemNameForHtmlId(
              props.itemName,
            )}-button`}
            color="primary"
            variant={props.variant}
            onClick={() => setIsModalVisible(true)}
            disabled={props.disabled}
            sx={
              props?.small
                ? {
                    width: "75%", // 50% of the default width
                    height: "75%", // 50% of the default height
                    padding: "4px 8px", // Adjust padding for smaller size
                  }
                : {}
            }
            style={{
              width: props.fullWidth && "100%",
              height: props.height && props.height,
              whiteSpace: props.noWrap && "nowrap",
              ...props.style,
            }}
            startIcon={props.icon}
          >
            {props.itemImageName ?? props.itemName}
          </Button>
        </div>
      </Tooltip>
      {isModalVisible &&
        React.createElement(props.createItemModal, {
          ...props,
          type: props.type,
          isVisible: isModalVisible,
          onClose: () => {
            setIsModalVisible(false);
          },
          ...props.createItemModalProps,
        })}
    </>
  );
};

CreateItemButton.propTypes = {
  itemName: PropTypes.string.isRequired,
  createItemModal: PropTypes.elementType.isRequired,
  // optional prop
  createItemModalProps: PropTypes.object,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  fullWidth: PropTypes.bool,
  height: PropTypes.string,
  tooltipTitle: PropTypes.string,
  style: PropTypes.any,
  noWrap: PropTypes.any,
  type: PropTypes.string,
  id: PropTypes.string,
  small: PropTypes.bool,
  itemImageName: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  variant: PropTypes.string,
};

CreateItemButton.defaultProps = {
  createItemModalProps: {},
  icon: <Add />,
  small: false,
  variant: "contained",
};

export const CreateItemButtonPage = (props) => {
  return (
    <Tooltip title={`Create ${props.itemName}`} placement="top" arrow>
      <div style={{ display: "inline-block" }}>
        <Link to={props.createItemLink}>
          <Button
            className={`create-${formatItemNameForHtmlId(
              props.itemName,
            )}-button`}
            data-testid={props["data-testid"]}
            color="primary"
            variant="contained"
            startIcon={<Add />}
          >
            {props.itemName}
          </Button>
        </Link>
      </div>
    </Tooltip>
  );
};

CreateItemButtonPage.propTypes = {
  itemName: PropTypes.string.isRequired,
  createItemLink: PropTypes.string,
};

export const UpdateAndDeleteButtonGroup = (props) => {
  const [isUpdateModalVisible, setIsUpdateModalVisible] = React.useState(false);
  const [isClearModalVisible, setIsClearModalVisible] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  return (
    <>
      {props.updateItemModal && (
        <Can
          perform={props.updateRole}
          yes={() => (
            <Tooltip title="Update" placement="top" arrow>
              <div style={{ display: "inline-block" }}>
                <CButton
                  id={`button-group-update-button-${
                    props?.row?.name ?? props.id
                  }`}
                  data-testid={`btn-update-${formatItemNameForHtmlId(
                    props.itemName,
                  )}-${props.id}`}
                  className={`update-${formatItemNameForHtmlId(
                    props.itemName,
                  )}-button button-animate-form`}
                  variant="outline"
                  color="dark"
                  size={props.buttonSize}
                  onClick={() => setIsUpdateModalVisible(true)}
                >
                  <div className="closed-bucket-button-group">
                    <CIcon
                      content={freeSet.cilPencil}
                      style={{ color: "#39f" }}
                    />
                    <span>Update</span>
                  </div>
                </CButton>
              </div>
            </Tooltip>
          )}
        />
      )}
      {props.clearItemModal && (
        <Can
          perform={props.clearRole}
          yes={() => (
            <Tooltip title="Clear" placement="top" arrow>
              <div style={{ display: "inline-block" }}>
                <CButton
                  className={`clear-${formatItemNameForHtmlId(
                    props.itemName,
                  )}-button button-animate-form`}
                  variant="outline"
                  color="dark"
                  size={props.buttonSize}
                  onClick={() => setIsClearModalVisible(true)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    <CIcon
                      content={freeSet.cilBackspace}
                      style={{ color: "#f9b115" }}
                    />
                    <span>Clear</span>
                  </div>
                </CButton>
              </div>
            </Tooltip>
          )}
        />
      )}
      <Can
        perform={props.deleteRole}
        yes={() => (
          <Tooltip title="Delete" placement="top" arrow>
            <div style={{ display: "inline-block" }}>
              <CButton
                className={`delete-${formatItemNameForHtmlId(
                  props.itemName,
                )}-button button-animate-form`}
                id="button-group-delete-button"
                data-testid={`btn-delete-${formatItemNameForHtmlId(
                  props.itemName,
                )}-${props.id}`}
                variant="outline"
                color="dark"
                size={props.buttonSize}
                onClick={() => setIsDeleteModalVisible(true)}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <CIcon
                    className="closed-bucket-button-group"
                    content={freeSet.cilTrash}
                    style={{ color: "#e55353" }}
                  />
                </div>
                <span>Delete</span>
              </CButton>
            </div>
          </Tooltip>
        )}
      />
      {props.updateItemModal &&
        React.createElement(props.updateItemModal, {
          id: props.id,
          name: props?.row?.name,
          isVisible: isUpdateModalVisible,
          onClose: () => {
            setIsUpdateModalVisible(false);
          },
          ...props.updateItemModalProps,
        })}
      {props.clearItemModal && // only render if clear modal is defined
        React.createElement(props.clearItemModal, {
          id: props.id,
          isVisible: isClearModalVisible,
          onClose: () => {
            setIsClearModalVisible(false);
          },
          ...props.clearItemModalProps,
        })}
      {React.createElement(props.deleteItemModal, {
        id: props.id,
        isVisible: isDeleteModalVisible,
        onClose: () => {
          setIsDeleteModalVisible(false);
        },
        ...props.deleteItemModalProps,
      })}
    </>
  );
};

UpdateAndDeleteButtonGroup.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  itemName: PropTypes.string.isRequired,
  updateItemModal: PropTypes.elementType,
  updateItemModalProps: PropTypes.object,
  clearItemModal: PropTypes.elementType,
  clearItemModalProps: PropTypes.object,
  deleteItemModal: PropTypes.elementType.isRequired,
  deleteItemModalProps: PropTypes.object,
  buttonSize: PropTypes.oneOf(["sm", "md", "lg"]),
  updateRole: PropTypes.string,
  clearRole: PropTypes.string,
  deleteRole: PropTypes.string.isRequired,
  marginLeft: PropTypes.any,
};

UpdateAndDeleteButtonGroup.defaultProps = {
  buttonSize: "md",
  updateItemModalProps: {},
  deleteItemModalProps: {},
  marginLeft: "25px",
};

export const DownloadUploadDeleteButtonGroup = (props) => {
  const [isDownloadModalVisible, setIsDownloadModalVisible] =
    React.useState(false);
  const downloadReportTemplate = async (row) => {
    // we want to prompt a download for the user
    const fileData = {
      url: `${nuclearn_api_url}/artifacts/${row.artifact_fk}/download`,
      httpHeaders: authorizedUser(),
    };
    // Construct the API URL
    const fileUrl = fileData.url;
    console.log(fileData.httpHeaders);
    // Fetch the file with the appropriate headers
    const response = await fetch(fileUrl, {
      method: "GET",
      headers: {
        Authorization: fileData.httpHeaders.Authorization,
      },
    });

    // Check if the response is okay
    if (response.ok) {
      // Convert the response into a Blob
      const blob = await response.blob();
      // print the blob filename
      console.log(blob.name);
      console.log(blob);

      // Create a URL for the Blob
      const blobUrl = URL.createObjectURL(blob);

      // Create a download link and trigger it
      const downloadLink = document.createElement("a");
      downloadLink.href = blobUrl;
      downloadLink.download = row.artifact_name;
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Clean up
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(blobUrl);
    } else {
      console.error("Failed to download the file:", response.statusText);
    }
  };
  return (
    <>
      <UpdateAndDeleteButtonGroup {...props} />
      {props.downloadRole && props.reportType === ReportType.GENERATIVE && (
        <Can
          perform={props.downloadRole}
          yes={() => (
            <Tooltip title="Download" placement="top" arrow>
              <div
                style={{
                  display: "inline-block",
                }}
              >
                <CButton
                  className={`download-${formatItemNameForHtmlId(
                    props.itemName,
                  )}-button button-animate-form`}
                  variant="outline"
                  color="dark"
                  size={props.buttonSize}
                  onClick={() => downloadReportTemplate(props.row)}
                >
                  <div className="closed-bucket-button-group">
                    <CIcon
                      content={freeSet.cilDataTransferDown}
                      style={{ color: "#17c2fa" }}
                    />
                    <span>Download</span>
                  </div>
                </CButton>
              </div>
            </Tooltip>
          )}
        />
      )}
      {props.downloadItemModal &&
        React.createElement(props.downloadItemModal, {
          id: props.id,
          isVisible: isDownloadModalVisible,
          onClose: () => {
            setIsDownloadModalVisible(false);
          },
          ...props.downloadItemModalProps,
        })}
    </>
  );
};

DownloadUploadDeleteButtonGroup.propTypes = {
  ...UpdateAndDeleteButtonGroup.propTypes,
  downloadItemModal: PropTypes.elementType,
  downloadItemModalProps: PropTypes.object,
  downloadRole: PropTypes.string,
};

DownloadUploadDeleteButtonGroup.defaultProps = {
  ...UpdateAndDeleteButtonGroup.defaultProps,
  downloadItemModalProps: {},
};

export const closeAndScrollToId = (id, onClose, componentHistory) => {
  // pause for user to see
  setTimeout(() => {
    onClose();
    setTimeout(() => {
      if (document.getElementById(id)) {
        componentHistory.push(`#${id}`);
        document.getElementById(id).scrollIntoView();
      }
    }, 500);
  }, 1000);
};

// eslint-disable-next-line no-unused-vars
const dateFormatter = (dateString) => {
  return format(new Date(dateString), "MM/dd/yyyy HH:mm O");
};
